import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import * as FileSaver from 'file-saver';

import { BasePage } from '../../base.page';
import { Contract, Document, FullClient } from '../../../domain/data';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { FullDataPage } from '../full-data.page';
import {
    ClientType,
    GetContractUseCase,
    GetDocumentContentUseCase,
    GetParticipantsUseCase,
    UpdateContractUseCase,
    UpdateValidationUseCase,
    UserInterface,
    ValidationStatus,
} from '../../../../core';
import { MainRoutes } from '../../../main-routes.enum';
import { ParentEvents } from '../../../utils/parent-events.service';
import { ValidationReasonDialog } from '../../../ui/dialog/validation-reason/validation-reason.dialog';

@Component({
    templateUrl: './full-data-account.page.html',
    styleUrls: ['./full-data-account.page.scss'],
})
export class FullDataAccountPage extends BasePage implements AfterViewInit, OnInit, OnDestroy {
    public MainRoutes: typeof MainRoutes = MainRoutes;
    public ClientType: typeof ClientType = ClientType;

    public contract: Contract;
    public participants: FullClient[] = [];
    public isFormFullDataAccountValid = false;


    private downloadedDocuments: string[] = [];

    @ViewChildren(NgForm)
    forms: QueryList<NgForm>;
    formChangesSubscription: Subscription;
    formStatusChangesSubscription: Subscription;

    constructor(
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private fullDataPage: FullDataPage,
        private getContractUseCase: GetContractUseCase,
        private getDocumentContentUseCase: GetDocumentContentUseCase,
        private getParticipantsUseCase: GetParticipantsUseCase,
        private router: Router,
        private updateContractUseCase: UpdateContractUseCase,
        private updateValidationUseCase: UpdateValidationUseCase
    ) {
        super(parentEvents);
        fullDataPage.showBackButton();
    }

    get user(): UserInterface {
        return this.fullDataPage.user;
    }

    get canFinalizeContract(): boolean {
        return (
            this.contract &&
            this.participants &&
            this.participants.length > 0 &&
            this.contract.isComplete() &&
            !this.participants.some((participant) => !participant.isComplete(this.contract.isAdvisoryModelGDC()))
        );
    }

    get canRevise(): boolean {
        return this.contract && !this.contract.isValidated();
    }

    get canValidate(): boolean {
        return (
            this.contract &&
            !this.contract.isValidated() &&
            this.contract.documents.filter((document) => !!document.id).length === this.downloadedDocuments.length
        );
    }

    ngOnInit() {
        this.getContract();
        this.getParticipants();
    }

    private getContract() {
        this.getContractUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.contract = transaction.data as Contract;
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    private getParticipants() {
        this.getParticipantsUseCase.bind({ isFull: true }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.participants = transaction.data as FullClient[];
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }
    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.handleFormStatusChanges();
    }

    private handleFormStatusChanges(): void {
        if (!this.formChangesSubscription) {
            const checkChanges = (form: NgForm) => {
                if (this.formStatusChangesSubscription) {
                    this.formStatusChangesSubscription.unsubscribe();
                }

                this.isFormFullDataAccountValid = form.valid;
                this.formStatusChangesSubscription = form.statusChanges.subscribe(() => {
                    this.isFormFullDataAccountValid = form.valid;
                });
            };

            if (this.forms.length > 0) {
                checkChanges(this.forms.first);
            }
            this.formChangesSubscription = this.forms.changes.subscribe((items) => {
                if (items.length > 0) {
                    checkChanges(items.first);
                }
            });
        }
    }

    ngOnDestroy() {
        if (this.formChangesSubscription) {
            this.formChangesSubscription.unsubscribe();
        }
        if (this.formStatusChangesSubscription) {
            this.formStatusChangesSubscription.unsubscribe();
        }
    }

    onAccordionChange() {
        this.parentEvents.resize();
    }

    onSubmit() {
        this.updateContractUseCase.bind({ contract: this.contract }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.router.navigateByUrl(MainRoutes.FULL_DATA);
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    onRevise() {
        const dialogRef = this.dialog.open(ValidationReasonDialog, {
            data: {
                text: 'dialog.revise-contract',
                validation: this.contract.validation,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.contract.validation.status = ValidationStatus.REVISE;
                this.updateValidationAndRedirect();
            }
        });
    }

    private updateValidationAndRedirect() {
        this.updateValidationUseCase.bind({ contract: this.contract }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.router.navigateByUrl(MainRoutes.FULL_DATA);
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    onValidate() {
        this.contract.validation.status = ValidationStatus.VALID;
        this.updateValidationAndRedirect();
    }

    onDocumentView(document: Document) {
        this.getDocumentContentUseCase.bind({ document }).subscribe((transaction) => {
            if (!!document.id && this.downloadedDocuments.indexOf(String(document.id)) === -1) {
                this.downloadedDocuments.push(String(document.id));
            }

            if (transaction.isSuccess) {
                FileSaver.saveAs(transaction.data, document.filename);
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    onDocumentRemove(document: Document) {
        if (document.id) {
            document.remove = true;
        }
    }
}
